import React, { useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { format } from 'date-fns';
import { useAuth } from '../contexts/AuthContext';
import { useTheme } from '../contexts/ThemeContext';
import { useNotifications } from '../contexts/NotificationContext';
import {
  FiHome,
  FiBookOpen,
  FiRotateCcw,
  FiBarChart3,
  FiUser,
  FiSun,
  FiMoon,
  FiBell,
  FiLogOut,
  FiSettings,
  FiMenu,
  FiX,
  FiCalendar,
  FiClock,
  FiAlertCircle,
  FiArrowRight,
} from 'react-icons/fi';

const Navbar = () => {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [isProfileOpen, setIsProfileOpen] = useState(false);
  const [isNotificationOpen, setIsNotificationOpen] = useState(false);
  
  const { user, logout } = useAuth();
  const { darkMode, toggleDarkMode } = useTheme();
  const { notifications } = useNotifications();
  const location = useLocation();
  const navigate = useNavigate();

  const navItems = [
    { path: '/dashboard', label: 'Dashboard', icon: FiHome },
    { path: '/problems', label: 'Problems', icon: FiBookOpen },
    { path: '/revision', label: 'Revision', icon: FiRotateCcw },
    { path: '/profile', label: 'Profile', icon: FiUser },
  ];

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  const totalNotifications = notifications.upcoming.length + notifications.overdue.length;

  return (
    <nav className="bg-white/95 dark:bg-gray-800/95 backdrop-blur-xl shadow-lg border-b border-gray-200/50 dark:border-gray-700/50 sticky top-0 z-40">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex justify-between h-16">
          {/* Logo and Navigation */}
          <div className="flex items-center">
            <Link to="/dashboard" className="flex items-center space-x-3">
              <div className="w-10 h-10 bg-gradient-to-r from-primary-600 to-purple-600 rounded-xl flex items-center justify-center shadow-lg">
                <FiBookOpen className="w-6 h-6 text-white" />
              </div>
              <span className="text-2xl font-bold text-gradient">CodeStreak</span>
            </Link>

            {/* Desktop Navigation */}
            <div className="hidden md:ml-10 md:flex md:space-x-8">
              {navItems.map((item) => {
                const Icon = item.icon;
                const isActive = location.pathname === item.path;
                
                return (
                  <Link
                    key={item.path}
                    to={item.path}
                    className={`inline-flex items-center px-1 pt-1 text-sm font-medium border-b-2 transition-colors duration-200 ${
                      isActive
                        ? 'border-primary-500 text-primary-600 dark:text-primary-400'
                        : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300 dark:text-gray-400 dark:hover:text-gray-300'
                    }`}
                  >
                    <Icon className="w-4 h-4 mr-2" />
                    {item.label}
                  </Link>
                );
              })}
            </div>
          </div>

          {/* Right side items */}
          <div className="flex items-center space-x-4">
            {/* Dark mode toggle */}
            <button
              onClick={toggleDarkMode}
              className="p-2 rounded-xl text-gray-500 hover:text-gray-700 hover:bg-gray-100 dark:text-gray-400 dark:hover:text-gray-300 dark:hover:bg-gray-700 transition-all duration-200 hover:scale-105"
            >
              {darkMode ? <FiSun className="w-5 h-5" /> : <FiMoon className="w-5 h-5" />}
            </button>

            {/* Notifications */}
            <div className="relative">
              <button
                onClick={() => setIsNotificationOpen(!isNotificationOpen)}
                className="p-2 rounded-xl text-gray-500 hover:text-gray-700 hover:bg-gray-100 dark:text-gray-400 dark:hover:text-gray-300 dark:hover:bg-gray-700 transition-all duration-200 relative hover:scale-105"
              >
                <FiBell className="w-5 h-5" />
                {totalNotifications > 0 && (
                  <span className="absolute -top-1 -right-1 bg-gradient-to-r from-red-500 to-red-600 text-white text-xs rounded-full h-5 w-5 flex items-center justify-center shadow-lg animate-pulse">
                    {totalNotifications}
                  </span>
                )}
              </button>

              {/* Enhanced Notification dropdown */}
              {isNotificationOpen && (
                <div className="absolute right-0 mt-2 w-96 bg-white/95 dark:bg-gray-800/95 backdrop-blur-xl rounded-2xl shadow-2xl border border-gray-200/50 dark:border-gray-700/50 z-50 max-h-[80vh] overflow-hidden">
                  <div className="p-6 border-b border-gray-200/50 dark:border-gray-700/50 bg-gradient-to-r from-primary-50/50 to-purple-50/50 dark:from-gray-800/50 dark:to-gray-700/50">
                    <div className="flex items-center justify-between">
                      <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100 flex items-center">
                        <FiBell className="w-5 h-5 mr-2 text-primary-600 dark:text-primary-400" />
                        Notifications
                      </h3>
                      <div className="flex items-center space-x-2">
                        {totalNotifications > 0 && (
                          <span className="bg-primary-100 dark:bg-primary-900/30 text-primary-700 dark:text-primary-300 px-3 py-1 rounded-full text-sm font-medium">
                            {totalNotifications} total
                          </span>
                        )}
                        <button
                          onClick={() => setIsNotificationOpen(false)}
                          className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 p-1 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors duration-200"
                        >
                          <FiX className="w-4 h-4" />
                        </button>
                      </div>
                    </div>
                  </div>
                  
                  <div className="max-h-96 overflow-y-auto scrollbar-thin">
                    {notifications.overdue.length > 0 && (
                      <div className="p-4 border-b border-gray-200/50 dark:border-gray-700/50">
                        <div className="flex items-center mb-4">
                          <div className="w-3 h-3 bg-red-500 rounded-full mr-3 animate-pulse"></div>
                          <h4 className="text-sm font-semibold text-red-700 dark:text-red-400 uppercase tracking-wide">
                            Overdue Revisions ({notifications.overdue.length})
                          </h4>
                        </div>
                        <div className="space-y-3">
                          {notifications.overdue.map((notification) => (
                            <div key={notification._id} className="group">
                              <div className="bg-red-50/80 dark:bg-red-900/20 backdrop-blur-sm rounded-xl p-4 border border-red-200/50 dark:border-red-800/30 hover:shadow-md transition-all duration-200 hover:bg-red-50 dark:hover:bg-red-900/30">
                                <div className="flex items-start justify-between">
                                  <div className="flex-1">
                                    <h5 className="font-medium text-gray-900 dark:text-gray-100 mb-1 group-hover:text-red-700 dark:group-hover:text-red-300 transition-colors duration-200">
                                      {notification.problemName}
                                    </h5>
                                    <div className="flex items-center space-x-3 text-sm text-gray-600 dark:text-gray-400 mb-2">
                                      <span className="flex items-center">
                                        <FiBookOpen className="w-3 h-3 mr-1" />
                                        {notification.platform || 'Platform'}
                                      </span>
                                      {(notification.dueDate || notification.nextRevisionDate) && (
                                        <span className="flex items-center">
                                          <FiCalendar className="w-3 h-3 mr-1" />
                                          Due: {
                                            (() => {
                                              const date = notification.dueDate || notification.nextRevisionDate;
                                              const dateObj = new Date(date);
                                              return !isNaN(dateObj.getTime()) ? format(dateObj, 'MMM dd, yyyy') : 'Invalid date';
                                            })()
                                          }
                                        </span>
                                      )}
                                    </div>
                                    <div className="flex items-center justify-between">
                                      <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-200">
                                        <FiAlertCircle className="w-3 h-3 mr-1" />
                                        Overdue
                                      </span>
                                      <button className="text-sm text-primary-600 hover:text-primary-700 dark:text-primary-400 dark:hover:text-primary-300 font-medium opacity-0 group-hover:opacity-100 transition-opacity duration-200">
                                        Review Now →
                                      </button>
                                    </div>
                                  </div>
                                </div>
                              </div>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}
                    
                    {notifications.upcoming.length > 0 && (
                      <div className="p-4">
                        <div className="flex items-center mb-4">
                          <div className="w-3 h-3 bg-yellow-500 rounded-full mr-3"></div>
                          <h4 className="text-sm font-semibold text-yellow-700 dark:text-yellow-400 uppercase tracking-wide">
                            Upcoming Revisions ({notifications.upcoming.length})
                          </h4>
                        </div>
                        <div className="space-y-3">
                          {notifications.upcoming.map((notification) => (
                            <div key={notification._id} className="group">
                              <div className="bg-yellow-50/80 dark:bg-yellow-900/20 backdrop-blur-sm rounded-xl p-4 border border-yellow-200/50 dark:border-yellow-800/30 hover:shadow-md transition-all duration-200 hover:bg-yellow-50 dark:hover:bg-yellow-900/30">
                                <div className="flex items-start justify-between">
                                  <div className="flex-1">
                                    <h5 className="font-medium text-gray-900 dark:text-gray-100 mb-1 group-hover:text-yellow-700 dark:group-hover:text-yellow-300 transition-colors duration-200">
                                      {notification.problemName}
                                    </h5>
                                    <div className="flex items-center space-x-3 text-sm text-gray-600 dark:text-gray-400 mb-2">
                                      <span className="flex items-center">
                                        <FiBookOpen className="w-3 h-3 mr-1" />
                                        {notification.platform || 'Platform'}
                                      </span>
                                      <span className="flex items-center">
                                        <FiCalendar className="w-3 h-3 mr-1" />
                                        Due: {format(new Date(notification.dueDate || notification.nextRevisionDate), 'MMM dd, yyyy')}
                                      </span>
                                    </div>
                                    <div className="flex items-center justify-between">
                                      <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-yellow-100 text-yellow-800 dark:bg-yellow-900/30 dark:text-yellow-200">
                                        <FiClock className="w-3 h-3 mr-1" />
                                        Upcoming
                                      </span>
                                      <button className="text-sm text-primary-600 hover:text-primary-700 dark:text-primary-400 dark:hover:text-primary-300 font-medium opacity-0 group-hover:opacity-100 transition-opacity duration-200">
                                        Schedule →
                                      </button>
                                    </div>
                                  </div>
                                </div>
                              </div>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}
                    
                    {totalNotifications === 0 && (
                      <div className="p-8 text-center">
                        <div className="w-16 h-16 bg-gray-100 dark:bg-gray-700 rounded-full flex items-center justify-center mx-auto mb-4">
                          <FiBell className="w-8 h-8 text-gray-400" />
                        </div>
                        <h4 className="text-lg font-medium text-gray-900 dark:text-gray-100 mb-2">
                          All caught up!
                        </h4>
                        <p className="text-gray-500 dark:text-gray-400 text-sm">
                          No pending revisions at the moment. Great work on staying consistent with your practice!
                        </p>
                      </div>
                    )}
                  </div>
                  
                  {totalNotifications > 0 && (
                    <div className="p-4 border-t border-gray-200/50 dark:border-gray-700/50 bg-gray-50/50 dark:bg-gray-800/50">
                      <div className="flex items-center justify-between">
                        <button className="text-sm text-gray-600 hover:text-gray-800 dark:text-gray-400 dark:hover:text-gray-200 font-medium transition-colors duration-200">
                          Mark all as read
                        </button>
                        <Link
                          to="/revision"
                          className="text-sm text-primary-600 hover:text-primary-700 dark:text-primary-400 dark:hover:text-primary-300 font-medium inline-flex items-center transition-colors duration-200"
                          onClick={() => setIsNotificationOpen(false)}
                        >
                          View all revisions
                          <FiArrowRight className="w-3 h-3 ml-1" />
                        </Link>
                      </div>
                    </div>
                  )}
                </div>
              )}
            </div>

            {/* Profile dropdown */}
            <div className="relative">
              <button
                onClick={() => setIsProfileOpen(!isProfileOpen)}
                className="flex items-center space-x-2 p-2 rounded-xl hover:bg-gray-100 dark:hover:bg-gray-700 transition-all duration-200 hover:scale-105"
              >
                <div className="w-8 h-8 bg-gradient-to-r from-primary-600 to-purple-600 rounded-full flex items-center justify-center shadow-lg">
                  {user?.profilePicture ? (
                    <img
                      src={user.profilePicture}
                      alt="Profile"
                      className="w-8 h-8 rounded-full object-cover"
                    />
                  ) : (
                    <span className="text-white text-sm font-medium">
                      {user?.name?.charAt(0).toUpperCase()}
                    </span>
                  )}
                </div>
                <span className="hidden md:block text-sm font-medium text-gray-700 dark:text-gray-300">
                  {user?.name}
                </span>
              </button>

              {/* Profile dropdown menu */}
              {isProfileOpen && (
                <div className="absolute right-0 mt-2 w-48 bg-white/95 dark:bg-gray-800/95 backdrop-blur-xl rounded-2xl shadow-2xl border border-gray-200/50 dark:border-gray-700/50 z-50">
                  <div className="py-2">
                    <Link
                      to="/profile"
                      className="flex items-center px-4 py-3 text-sm text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700 rounded-xl mx-2 transition-colors duration-200"
                      onClick={() => setIsProfileOpen(false)}
                    >
                      <FiUser className="w-4 h-4 mr-3" />
                      Profile
                    </Link>
                    <Link
                      to="/profile"
                      className="flex items-center px-4 py-3 text-sm text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700 rounded-xl mx-2 transition-colors duration-200"
                      onClick={() => setIsProfileOpen(false)}
                    >
                      <FiSettings className="w-4 h-4 mr-3" />
                      Settings
                    </Link>
                    <button
                      onClick={handleLogout}
                      className="flex items-center w-full px-4 py-3 text-sm text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700 rounded-xl mx-2 transition-colors duration-200"
                    >
                      <FiLogOut className="w-4 h-4 mr-3" />
                      Logout
                    </button>
                  </div>
                </div>
              )}
            </div>

            {/* Mobile menu button */}
            <button
              onClick={() => setIsMenuOpen(!isMenuOpen)}
              className="md:hidden p-2 rounded-xl text-gray-500 hover:text-gray-700 hover:bg-gray-100 dark:text-gray-400 dark:hover:text-gray-300 dark:hover:bg-gray-700 transition-all duration-200 hover:scale-105"
            >
              {isMenuOpen ? <FiX className="w-6 h-6" /> : <FiMenu className="w-6 h-6" />}
            </button>
          </div>
        </div>

        {/* Mobile Navigation */}
        {isMenuOpen && (
          <div className="md:hidden">
            <div className="px-2 pt-2 pb-3 space-y-1 sm:px-3 border-t border-gray-200/50 dark:border-gray-700/50 bg-gray-50/30 dark:bg-gray-800/30 backdrop-blur-sm rounded-b-2xl">
              {navItems.map((item) => {
                const Icon = item.icon;
                const isActive = location.pathname === item.path;
                
                return (
                  <Link
                    key={item.path}
                    to={item.path}
                    className={`flex items-center px-3 py-2 text-base font-medium rounded-xl transition-colors duration-200 ${
                      isActive
                        ? 'bg-primary-50 text-primary-700 dark:bg-primary-900 dark:text-primary-300'
                        : 'text-gray-600 hover:text-gray-900 hover:bg-gray-50 dark:text-gray-400 dark:hover:text-gray-300 dark:hover:bg-gray-700'
                    }`}
                    onClick={() => setIsMenuOpen(false)}
                  >
                    <Icon className="w-5 h-5 mr-3" />
                    {item.label}
                  </Link>
                );
              })}
            </div>
          </div>
        )}
      </div>
    </nav>
  );
};

export default Navbar;